import { useState } from 'react';
import { complianceApi, type EWayBillRecord } from '@/api/compliance.api';
import { parseEWBErrors } from '@/utils/ewbErrorCodes';
import { useToast } from '@/contexts/ToastContext';

interface Props {
  ewb: EWayBillRecord;
  onClose: () => void;
  onUpdated: () => void;
}

const VEHICLE_REASONS = [
  { code: '1', label: '1 - Due to Break down' },
  { code: '2', label: '2 - Due to Transhipment' },
  { code: '3', label: '3 - Others (Change in Vehicle)' },
  { code: '4', label: '4 - First Time / Updating Part B' },
];

const TRANSPORT_MODES = [
  { value: '1', label: 'Road', icon: 'ri-truck-line' },
  { value: '2', label: 'Rail', icon: 'ri-train-line' },
  { value: '3', label: 'Air', icon: 'ri-flight-takeoff-line' },
  { value: '4', label: 'Ship', icon: 'ri-ship-line' },
];

const INDIAN_STATES = [
  { code: '01', name: 'Jammu & Kashmir (01)' },
  { code: '02', name: 'Himachal Pradesh (02)' },
  { code: '03', name: 'Punjab (03)' },
  { code: '04', name: 'Chandigarh (04)' },
  { code: '05', name: 'Uttarakhand (05)' },
  { code: '06', name: 'Haryana (06)' },
  { code: '07', name: 'Delhi (07)' },
  { code: '08', name: 'Rajasthan (08)' },
  { code: '09', name: 'Uttar Pradesh (09)' },
  { code: '10', name: 'Bihar (10)' },
  { code: '11', name: 'Sikkim (11)' },
  { code: '12', name: 'Arunachal Pradesh (12)' },
  { code: '13', name: 'Nagaland (13)' },
  { code: '14', name: 'Manipur (14)' },
  { code: '15', name: 'Mizoram (15)' },
  { code: '16', name: 'Tripura (16)' },
  { code: '17', name: 'Meghalaya (17)' },
  { code: '18', name: 'Assam (18)' },
  { code: '19', name: 'West Bengal (19)' },
  { code: '20', name: 'Jharkhand (20)' },
  { code: '21', name: 'Odisha (21)' },
  { code: '22', name: 'Chhattisgarh (22)' },
  { code: '23', name: 'Madhya Pradesh (23)' },
  { code: '24', name: 'Gujarat (24)' },
  { code: '25', name: 'Daman & Diu (25)' },
  { code: '26', name: 'Dadra & Nagar Haveli (26)' },
  { code: '27', name: 'Maharashtra (27)' },
  { code: '29', name: 'Karnataka (29)' },
  { code: '30', name: 'Goa (30)' },
  { code: '31', name: 'Lakshadweep (31)' },
  { code: '32', name: 'Kerala (32)' },
  { code: '33', name: 'Tamil Nadu (33)' },
  { code: '34', name: 'Puducherry (34)' },
  { code: '35', name: 'Andaman & Nicobar (35)' },
  { code: '36', name: 'Telangana (36)' },
  { code: '37', name: 'Andhra Pradesh (37)' },
];

export default function UpdateVehicleSlideOver({ ewb, onClose, onUpdated }: Props) {
  const toast = useToast();

  const defaultState = ewb.fromGSTIN ? ewb.fromGSTIN.substring(0, 2) : '27';
  const defaultPlace = ewb.fromAddress?.split(',')[0]?.trim() || '';

  const [transMode, setTransMode]       = useState<'1' | '2' | '3' | '4'>('1');
  const [vehicleNo, setVehicleNo]       = useState(ewb.vehicleNumber || '');
  const [fromPlace, setFromPlace]       = useState(defaultPlace);
  const [fromState, setFromState]       = useState(defaultState);
  const [reasonCode, setReasonCode]     = useState('2');
  const [reasonRem, setReasonRem]       = useState('Transhipment');
  const [transDocNo, setTransDocNo]     = useState('');
  const [transDocDate, setTransDocDate] = useState('');
  const [loading, setLoading]           = useState(false);

  const handleReasonChange = (code: string) => {
    setReasonCode(code);
    const found = VEHICLE_REASONS.find(r => r.code === code);
    if (found) {
      setReasonRem(found.label.replace(/^\d\s*-\s*/, ''));
    }
  };

  const handleVehicleNoChange = (val: string) => {
    setVehicleNo(val.toUpperCase().replace(/[^A-Z0-9]/g, ''));
  };

  const canSubmit = Boolean(
    ewb.ewbNumber &&
    fromPlace.trim() &&
    fromState &&
    reasonCode &&
    reasonRem.trim() &&
    (transMode !== '1' || vehicleNo.trim() || transDocNo.trim())
  );

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);

    try {
      const payload = {
        ewbId: ewb.id,
        ewbNo: ewb.ewbNumber,
        vehicleNo: transMode === '1' && vehicleNo.trim() ? vehicleNo.trim() : undefined,
        fromPlace: fromPlace.trim(),
        fromState: Number(fromState),
        reasonCode,
        reasonRem: reasonRem.trim(),
        transMode,
        transDocNo: transDocNo.trim() || undefined,
        transDocDate: transDocDate.trim() || undefined,
      };

      const res = await complianceApi.updateVehicle(payload);

      if (res?.success) {
        toast.success(res.message || 'Vehicle / Part-B updated successfully ✓');
        onUpdated();
        onClose();
      } else {
        const parsedErr = parseEWBErrors(res?.errorDetails || res?.message);
        if (parsedErr?.errors?.length > 0) {
          parsedErr.errors.forEach(err => {
            toast.error(err.fix ? `${err.message} — ${err.fix}` : err.message);
          });
        } else {
          toast.error(parsedErr.userMessage || res?.message || 'Failed to update vehicle');
        }
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update vehicle');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-2xl overflow-y-auto">
        
        {/* Header */}
        <div className="sticky top-0 z-10 bg-slate-900 text-white px-6 py-4 flex items-center justify-between shadow-sm">
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-500 text-white uppercase tracking-wider">
                Part-B Update
              </span>
              <h2 className="text-base font-semibold">Update Vehicle / Transport Details</h2>
            </div>
            <p className="text-xs text-slate-400 mt-0.5 font-mono">
              EWB #{ewb.ewbNumber} • {ewb.invoiceNumber || 'Document'}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white cursor-pointer transition-colors">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-6 space-y-6">

          {/* Current Info Banner */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2 text-xs">
            <div className="flex items-center justify-between text-slate-600">
              <span>Customer / Recipient</span>
              <span className="font-semibold text-slate-900">{ewb.customerName || '—'}</span>
            </div>
            <div className="flex items-center justify-between text-slate-600">
              <span>Current Vehicle No.</span>
              <span className="font-mono font-bold text-indigo-600">{ewb.vehicleNumber || 'Not Assigned (Part-A Only)'}</span>
            </div>
            <div className="flex items-center justify-between text-slate-600">
              <span>Transport Mode</span>
              <span className="font-medium text-slate-900">{ewb.transportMode || 'Road'}</span>
            </div>
          </div>

          {/* Transport Mode */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
              Transport Mode <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-4 gap-2">
              {TRANSPORT_MODES.map(m => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setTransMode(m.value as any)}
                  className={`flex flex-col items-center justify-center gap-1.5 p-2.5 rounded-lg border text-xs font-medium cursor-pointer transition-all ${
                    transMode === m.value
                      ? 'bg-indigo-50 border-indigo-500 text-indigo-700 font-semibold shadow-xs'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <i className={`${m.icon} text-base`} />
                  <span>{m.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Vehicle Number (if road) */}
          {transMode === '1' && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  New Vehicle Number <span className="text-red-500">*</span>
                </label>
                <span className="text-[11px] text-slate-400 font-mono">e.g. KA01AB1234</span>
              </div>
              <input
                type="text"
                value={vehicleNo}
                onChange={(e) => handleVehicleNoChange(e.target.value)}
                placeholder="KA01AB1234"
                maxLength={15}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 uppercase font-mono font-medium"
              />
              <p className="mt-1 text-[11px] text-slate-400">
                Format: 2-digit state code + 2-digit RTO + series + 4-digit number (no spaces).
              </p>
            </div>
          )}

          {/* Place & State of Dispatch / Change */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                From Place (City) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={fromPlace}
                onChange={(e) => setFromPlace(e.target.value)}
                placeholder="e.g. Bangalore"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
              />
              <p className="mt-1 text-[11px] text-slate-400">City where vehicle change occurs</p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                From State <span className="text-red-500">*</span>
              </label>
              <select
                value={fromState}
                onChange={(e) => setFromState(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 cursor-pointer bg-white"
              >
                {INDIAN_STATES.map(s => (
                  <option key={s.code} value={s.code}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Reason Code */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
              Reason for Change <span className="text-red-500">*</span>
            </label>
            <select
              value={reasonCode}
              onChange={(e) => handleReasonChange(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 cursor-pointer bg-white"
            >
              {VEHICLE_REASONS.map(r => (
                <option key={r.code} value={r.code}>{r.label}</option>
              ))}
            </select>
          </div>

          {/* Reason Remarks */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
              Reason Remarks <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={reasonRem}
              onChange={(e) => setReasonRem(e.target.value)}
              placeholder="e.g. Transhipment due to route consolidation"
              maxLength={50}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            />
          </div>

          {/* Transport Document (LR / RR / Airway Bill / Bill of Lading) */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
            <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
              Transport Document <span className="text-slate-400 font-normal">(Optional)</span>
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-600 mb-1">
                  {transMode === '1' ? 'LR / GR Number' : transMode === '2' ? 'RR Number' : transMode === '3' ? 'Airway Bill No' : 'Bill of Lading No'}
                </label>
                <input
                  type="text"
                  value={transDocNo}
                  onChange={(e) => setTransDocNo(e.target.value)}
                  placeholder="e.g. LR-987654"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-mono bg-white"
                />
              </div>

              <div>
                <label className="block text-xs text-slate-600 mb-1">Document Date</label>
                <input
                  type="date"
                  value={transDocDate}
                  onChange={(e) => setTransDocDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 cursor-pointer bg-white"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 cursor-pointer transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || loading}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2 shadow-xs transition-colors"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Updating Vehicle...
                </>
              ) : (
                <>
                  <i className="ri-check-line" />
                  Update Vehicle (Part-B)
                </>
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
